import React from 'react';
import {
  ArrowUpRight,
  BookOpenText,
  Bot,
  BrainCircuit,
  BriefcaseBusiness,
  CheckCircle2,
  FileSpreadsheet,
  Github,
  GraduationCap,
  Landmark,
  LayoutGrid,
  Linkedin,
  MessageSquareText,
  Scale,
  ShieldCheck,
  Sparkles,
  Workflow,
} from 'lucide-react';
import { APPLICATION_LINKS } from '../constants';

const LINKEDIN_URL = 'https://www.linkedin.com/';

const taxCanvasPro = APPLICATION_LINKS[0];
const aussieLedger = APPLICATION_LINKS[1];

const expertisePillars = [
  {
    title: 'Tax',
    text: 'Australian tax structuring, distributions, workpapers, return preparation workflows, and practical advisory context.',
    icon: Scale,
  },
  {
    title: 'Law',
    text: 'Entity relationships, obligations, documentation trails, compliance reasoning, and professional risk framing.',
    icon: Landmark,
  },
  {
    title: 'Accounting',
    text: 'Ledgers, journals, audit trails, source data, bookkeeping flows, and exportable records.',
    icon: FileSpreadsheet,
  },
  {
    title: 'Finance',
    text: 'Scenario modelling, capital flows, decision support, retained earnings, and commercial outcomes.',
    icon: BriefcaseBusiness,
  },
  {
    title: 'AI',
    text: 'AI-native interfaces, structured reasoning, professional workflow automation, and explainable systems.',
    icon: BrainCircuit,
  },
];

const thesisPrinciples = [
  {
    title: 'Domain depth beats generic automation',
    text: 'Professional services need systems that understand the shape of tax, law, accounting, and finance work.',
  },
  {
    title: 'Professional AI needs structure',
    text: 'Good outputs depend on context, source data, records, controls, and reasoning paths that can be reviewed.',
  },
  {
    title: 'Tools must preserve judgement',
    text: 'The goal is not to replace professionals. It is to make expert reasoning more visible, useful, and scalable.',
  },
];

const proofItems = [
  {
    title: 'Tools built',
    text: 'TaxCanvasPro, AussieLedger, and AI-native workflow prototypes that turn professional logic into software.',
    icon: LayoutGrid,
  },
  {
    title: 'Panels and discussions',
    text: 'Public conversations on AI, professional services, and the practical future of expert work.',
    icon: MessageSquareText,
  },
  {
    title: 'Presentations and training',
    text: 'Training, enablement, and practical sessions translating frontier AI into professional capability.',
    icon: GraduationCap,
  },
  {
    title: 'Credentials and learning',
    text: 'Certifications and structured learning can be added here as the public profile expands.',
    icon: ShieldCheck,
  },
];

const buildPanels = [
  {
    name: 'TaxCanvasPro',
    href: taxCanvasPro.href,
    label: 'Selected build',
    title: 'A drag-and-drop tax structuring canvas with AI analysis.',
    text: 'TaxCanvasPro explores what professional AI looks like when tax structures are visual, connected, reviewable, and capable of being analysed by an AI layer.',
    proof: 'Professional AI needs visual structure, not just prompts.',
    cta: 'Explore TaxCanvasPro',
    icon: Workflow,
  },
  {
    name: 'AussieLedger',
    href: aussieLedger.href,
    label: 'Open-source build',
    title: 'A local-first accounting journal for Australian tax workflows.',
    text: 'AussieLedger focuses on reliable source data: journals, bookkeeping records, browser-local data, and exports that can support Australian tax preparation.',
    proof: 'Accounting AI needs dependable records before it can reason well.',
    cta: 'Explore AussieLedger',
    icon: FileSpreadsheet,
    sourceHref: aussieLedger.sourceHref,
  },
];

const insightTopics = [
  'Why professional AI needs structured data',
  'The difference between prompting and systems',
  'How tax structuring tools can learn from software architecture',
  'Why local-first finance tools matter',
  'AI governance for professional services',
  'Legal reasoning, audit trails, and explainable workflows',
];

const DomainMap = () => {
  const domains = [
    { label: 'Tax', className: 'domain-tax', icon: Scale },
    { label: 'Law', className: 'domain-law', icon: Landmark },
    { label: 'Accounting', className: 'domain-accounting', icon: FileSpreadsheet },
    { label: 'Finance', className: 'domain-finance', icon: BriefcaseBusiness },
    { label: 'AI Systems', className: 'domain-ai', icon: BrainCircuit },
    { label: 'Software', className: 'domain-software', icon: Bot },
  ];

  const outputs = [
    { label: 'TaxCanvasPro', className: 'output-taxcanvas' },
    { label: 'AussieLedger', className: 'output-ledger' },
    { label: 'Training', className: 'output-training' },
    { label: 'Notes', className: 'output-notes' },
  ];

  return (
    <div className="relative min-h-[560px] overflow-hidden rounded-[2.25rem] border border-primary-100 bg-white/80 p-5 shadow-2xl shadow-primary-900/10 backdrop-blur">
      <div className="absolute inset-0 bg-[radial-gradient(#CAB9D9_1px,transparent_1px)] [background-size:24px_24px] opacity-60" />
      <div className="domain-ring domain-ring-one" />
      <div className="domain-ring domain-ring-two" />

      <div className="domain-core">
        <Sparkles className="mb-3 h-7 w-7 text-primary" />
        <div className="text-[10px] font-black uppercase tracking-[0.24em] text-primary-400">
          Core Thesis
        </div>
        <div className="mt-2 text-xl font-black leading-tight text-primary-950">
          AI-native professional systems
        </div>
      </div>

      {domains.map((domain) => (
        <div key={domain.label} className={`domain-node ${domain.className}`}>
          <domain.icon className="h-5 w-5 text-primary" />
          <span>{domain.label}</span>
        </div>
      ))}

      {outputs.map((output) => (
        <div key={output.label} className={`output-node ${output.className}`}>
          {output.label}
        </div>
      ))}
    </div>
  );
};

export const Home: React.FC = () => {
  return (
    <div className="relative overflow-hidden bg-beige-100">
      <section className="relative min-h-screen overflow-hidden border-b border-primary-100/50 pt-28 grainy">
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(139,114,190,0.08)_1px,transparent_1px),linear-gradient(rgba(139,114,190,0.08)_1px,transparent_1px)] [background-size:56px_56px]" />

        <div className="container relative z-10 mx-auto grid min-h-[calc(100vh-7rem)] grid-cols-1 items-center gap-14 px-6 py-16 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="max-w-4xl">
            <div className="mb-8 inline-flex items-center gap-3 rounded-full border border-primary-100 bg-white/85 px-4 py-2 text-[10px] font-black uppercase tracking-[0.24em] text-primary-700 shadow-sm">
              <span className="flex h-2 w-2 rounded-full bg-emerald-500" />
              AI for professional services
            </div>

            <h1 className="max-w-4xl text-5xl font-black leading-[0.95] tracking-normal text-brandText md:text-7xl">
              Building AI-native systems for professional judgement.
            </h1>

            <p className="mt-8 max-w-3xl text-xl font-medium leading-relaxed text-primary-800 md:text-2xl">
              I work at the intersection of Australian tax, accounting, law, finance, and applied
              AI, translating professional knowledge into structured, explainable, and useful
              software systems.
            </p>

            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <a
                href={LINKEDIN_URL}
                className="group inline-flex items-center justify-center gap-3 rounded-2xl bg-primary-900 px-8 py-5 text-sm font-black uppercase tracking-widest text-white shadow-2xl shadow-primary-900/20 transition-all hover:-translate-y-0.5 hover:bg-primary-800"
              >
                Connect on LinkedIn
                <Linkedin className="h-5 w-5" />
              </a>
              <a
                href="#thesis"
                className="inline-flex items-center justify-center gap-3 rounded-2xl border border-primary-100 bg-white px-8 py-5 text-sm font-black uppercase tracking-widest text-primary-950 shadow-sm transition-all hover:border-primary-300"
              >
                Explore My Work
                <ArrowUpRight className="h-5 w-5" />
              </a>
            </div>

            <div className="mt-12 grid max-w-3xl grid-cols-1 gap-3 sm:grid-cols-3">
              {['Tax + AI', 'Professional systems', 'Builder and educator'].map((label) => (
                <div
                  key={label}
                  className="rounded-2xl border border-primary-100 bg-white/75 p-4 text-sm font-black uppercase tracking-widest text-primary-900"
                >
                  {label}
                </div>
              ))}
            </div>
          </div>

          <DomainMap />
        </div>
      </section>

      <section id="thesis" className="border-b border-primary-100/50 bg-white py-24">
        <div className="container mx-auto px-6">
          <div className="mx-auto max-w-5xl">
            <div className="mb-4 text-[10px] font-black uppercase tracking-[0.3em] text-primary">
              The Thesis
            </div>
            <h2 className="text-4xl font-black leading-tight tracking-normal text-primary-950 md:text-6xl">
              Professional AI will be won by domain experts who can build.
            </h2>
            <p className="mt-8 text-xl font-medium leading-relaxed text-primary-800 md:text-2xl">
              The next generation of professional AI will not be built by generic automation alone.
              Tax, law, accounting, and finance require structured reasoning, source data, audit
              trails, and judgement that can be explained. The opportunity is to encode domain
              expertise into systems that professionals can trust, inspect, and improve.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {thesisPrinciples.map((principle) => (
              <div
                key={principle.title}
                className="rounded-[1.5rem] border border-primary-100 bg-beige-100/50 p-7"
              >
                <CheckCircle2 className="mb-7 h-7 w-7 text-primary" />
                <h3 className="text-xl font-black text-primary-950">{principle.title}</h3>
                <p className="mt-4 font-medium leading-relaxed text-primary-800">
                  {principle.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="expertise" className="border-b border-primary-100/50 bg-beige-100 py-24">
        <div className="container mx-auto px-6">
          <div className="mb-14 max-w-4xl">
            <div className="mb-4 text-[10px] font-black uppercase tracking-[0.3em] text-primary">
              Domain Expertise
            </div>
            <h2 className="text-4xl font-black leading-tight tracking-normal text-primary-950 md:text-6xl">
              The domains I work across.
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-5">
            {expertisePillars.map((pillar) => (
              <div
                key={pillar.title}
                className="rounded-[1.5rem] border border-primary-100 bg-white/75 p-6"
              >
                <pillar.icon className="mb-6 h-7 w-7 text-primary" />
                <h3 className="text-xl font-black text-primary-950">{pillar.title}</h3>
                <p className="mt-4 text-sm font-medium leading-relaxed text-primary-800">
                  {pillar.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="story" className="border-b border-primary-100/50 bg-white py-24">
        <div className="container mx-auto grid grid-cols-1 gap-12 px-6 lg:grid-cols-[0.78fr_1.22fr]">
          <div>
            <div className="mb-4 text-[10px] font-black uppercase tracking-[0.3em] text-primary">
              The Story
            </div>
            <h2 className="text-4xl font-black leading-tight tracking-normal text-primary-950 md:text-6xl">
              Why I build in this space.
            </h2>
          </div>

          <div className="space-y-7 text-xl font-medium leading-relaxed text-primary-800">
            <p>
              Professional services are knowledge-heavy but workflow-fragmented. The real work is
              rarely a simple chat prompt; it is a chain of assumptions, records, obligations,
              judgements, and review points.
            </p>
            <p>
              I build tools to explore how that knowledge can become structured software: visual tax
              structures, local accounting data, explainable AI analysis, and exports that fit
              professional review.
            </p>
            <p>
              The work sits between domain expertise and applied AI: understanding enough of the
              professional logic to design systems that are not just impressive, but useful.
            </p>
          </div>
        </div>
      </section>

      <section id="proof" className="border-b border-primary-100/50 bg-beige-100 py-24">
        <div className="container mx-auto px-6">
          <div className="mb-14 max-w-4xl">
            <div className="mb-4 text-[10px] font-black uppercase tracking-[0.3em] text-primary">
              Proof Of Work
            </div>
            <h2 className="text-4xl font-black leading-tight tracking-normal text-primary-950 md:text-6xl">
              Building, teaching, and participating in the conversation.
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
            {proofItems.map((item) => (
              <div
                key={item.title}
                className="rounded-[1.5rem] border border-primary-100 bg-white p-7"
              >
                <item.icon className="mb-7 h-8 w-8 text-primary" />
                <h3 className="text-xl font-black text-primary-950">{item.title}</h3>
                <p className="mt-4 font-medium leading-relaxed text-primary-800">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="builds" className="border-b border-primary-100/50 bg-white py-24">
        <div className="container mx-auto px-6">
          <div className="mb-14 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
            <div className="max-w-4xl">
              <div className="mb-4 text-[10px] font-black uppercase tracking-[0.3em] text-primary">
                Selected Builds
              </div>
              <h2 className="text-4xl font-black leading-tight tracking-normal text-primary-950 md:text-6xl">
                Tools as evidence of the thinking.
              </h2>
            </div>
            <p className="max-w-md text-lg font-medium leading-relaxed text-primary-800">
              These products are not the whole story. They are working proof of a thesis about
              professional AI: domain logic should become structured, reviewable systems.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {buildPanels.map((build) => (
              <div
                key={build.name}
                className="rounded-[2rem] border border-primary-100 bg-beige-100/60 p-8"
              >
                <div className="mb-8 flex items-start justify-between gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-primary-100 bg-white text-primary">
                    <build.icon className="h-7 w-7" />
                  </div>
                  <span className="rounded-full border border-primary-100 bg-white px-3 py-1 text-[10px] font-black uppercase tracking-widest text-primary-700">
                    {build.label}
                  </span>
                </div>
                <h3 className="text-3xl font-black text-primary-950">{build.name}</h3>
                <p className="mt-4 text-xl font-black leading-tight text-primary-900">
                  {build.title}
                </p>
                <p className="mt-5 font-medium leading-relaxed text-primary-800">{build.text}</p>
                <div className="mt-7 rounded-2xl border border-primary-100 bg-white p-5 text-sm font-black uppercase tracking-widest text-primary-700">
                  {build.proof}
                </div>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <a
                    href={build.href}
                    className="inline-flex items-center justify-center gap-3 rounded-2xl bg-primary-900 px-6 py-4 text-sm font-black uppercase tracking-widest text-white transition-all hover:-translate-y-0.5 hover:bg-primary-800"
                  >
                    {build.cta}
                    <ArrowUpRight className="h-5 w-5" />
                  </a>
                  {build.sourceHref && (
                    <a
                      href={build.sourceHref}
                      className="inline-flex items-center justify-center gap-3 rounded-2xl border border-primary-100 bg-white px-6 py-4 text-sm font-black uppercase tracking-widest text-primary-950 transition-all hover:border-primary-300"
                    >
                      <Github className="h-5 w-5" />
                      View Source
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="notes" className="border-b border-primary-100/50 bg-beige-100 py-24">
        <div className="container mx-auto px-6">
          <div className="mb-14 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
            <div className="max-w-4xl">
              <div className="mb-4 text-[10px] font-black uppercase tracking-[0.3em] text-primary">
                Knowledge Hub
              </div>
              <h2 className="text-4xl font-black leading-tight tracking-normal text-primary-950 md:text-6xl">
                Thinking out loud about AI-native professional work.
              </h2>
            </div>
            <p className="max-w-md text-lg font-medium leading-relaxed text-primary-800">
              A publishing surface for notes, frameworks, diagrams, training material, and practical
              analysis.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {insightTopics.map((topic) => (
              <div
                key={topic}
                className="rounded-[1.25rem] border border-primary-100 bg-white/75 p-6"
              >
                <BookOpenText className="mb-5 h-6 w-6 text-primary" />
                <h3 className="text-lg font-black leading-snug text-primary-950">{topic}</h3>
                <p className="mt-4 text-sm font-medium leading-relaxed text-primary-700">
                  Placeholder for a future note, article, diagram, or briefing.
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="capability" className="bg-white py-24">
        <div className="container mx-auto px-6">
          <div className="rounded-[2rem] border border-primary-100 bg-primary-950 p-8 text-white md:p-12">
            <div className="flex flex-col justify-between gap-10 lg:flex-row lg:items-center">
              <div className="max-w-4xl">
                <div className="mb-5 text-[10px] font-black uppercase tracking-[0.3em] text-primary-300">
                  Professional Capability
                </div>
                <h2 className="text-3xl font-black leading-tight tracking-normal md:text-5xl">
                  I translate frontier AI into practical professional capability.
                </h2>
                <p className="mt-6 max-w-3xl text-lg font-medium leading-relaxed text-primary-100">
                  Through tools, panels, presentations, and training, I explore how AI changes the
                  work of tax, accounting, legal, and finance professionals.
                </p>
              </div>
              <a
                href={LINKEDIN_URL}
                className="inline-flex shrink-0 items-center justify-center gap-3 rounded-2xl bg-white px-8 py-5 text-sm font-black uppercase tracking-widest text-primary-950 transition-all hover:-translate-y-0.5"
              >
                Connect on LinkedIn
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
